import { Action, Reducer } from 'redux';

import {
    IdName,
    RestaurantGroup,
    RestaurantPaymentDetailsModel,
    TaxResponseModel,
} from 'api/models';
import { RoleData } from '../rolesStore/reducer';
import { RestaurantUserData } from '../usersStore/reducer';
import { RestaurantDetailsAction } from './actions';
import { OrderServices, Positions, TermsContentType } from 'constants/enums';
import { ServiceValue } from 'components/restaurantInformation/components/types';

export interface RestaurantDetailsState {
    restaurant: RestaurantDetails;
    restaurantInformation: RestaurantDetails;
    cuisineTypes: CuisineData[];
    taxes: TaxResponseModel;
    qrCodes: QRData[];
    qrCodesLoading: boolean;
    responsibleUsers: ResponsibleUser[];
    stornoReasons: IdName[];
    groups: RestaurantGroup[];
    managedGroups: RestaurantGroup[];
}

export interface CuisineData {
    id: string;
    name: string;
    inUse: number;
}

export interface RestaurantContactPerson {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    isEnabled: boolean;
    restaurants: RestaurantUserData[];
    roles: RoleData[];
}

export interface OrderOverviewPrintoutSettings {
    isEnabled: boolean;
    printingStations: string[];
}

export interface RestaurantAdvancedSettings {
    isPrintingEnabled: boolean;
    orderOverviewPrintout: OrderOverviewPrintoutSettings;
    receiptLogoUrl?: string;
}

export interface RestaurantDetails {
    id: string;
    name: string;
    addressLine: string;
    zip: string;
    city: string;
    phoneNumber: string;
    email: string;
    website: string;
    latitude: number;
    longitude: number;
    contactPersons: RestaurantContactPerson[];
    logoUrl?: string;
    photoUrl?: string;
    percentageForDineIn?: number;
    percentageForTakeAway?: number;
    percentageForHomeDelivery?: number;
    paymentDetails: PaymentDetails;
    timeZone: string;
    openHours?: any[];
    qrCodesData: QRCodesData;
    homeDelivery: ServiceValue;
    takeAway: ServiceValue;
    dineIn: ServiceValue;
    takeOut: ServiceValue;
    pricingLevels: IdName[];
    groupName?: string;
    isGroupMaster?: boolean;
    isGroupManager?: boolean;
    groupPricingLevels?: IdName[];
    managedGroupPricingLevels?: IdName[];
    coolBoxData: CoolBoxData;
    type: any[];
    isDeliverAnytime: boolean;
    advancedSettings?: RestaurantAdvancedSettings;
    termsAndConditions: TermsAndConditions;
    description: string;
    facebook?: string;
    instagram?: string;
    addToGallery?: boolean;
    ordersDeadline?: number;
    isPayToClose?: boolean;
    isOrderInAdvance?: boolean;
}

export interface CoolBoxData {
    coolBoxName: string;
    isCoolBox: boolean;
    isPublic: boolean;
}

export interface QRCodesData {
    dineInQrCodeName: string;
    takeAwayQrCodeName: string;
    homeDeliveryQrName: string;
}

export interface QRData {
    id?: string;
    name: string;
    service: OrderServices;
    url: string;
    area: string;
    areaId?: string;
}

export interface ResponsibleUser {
    id: string;
    position: Positions;
    name: string;
}

export interface PaymentDetails extends RestaurantPaymentDetailsModel {}

export interface TermsAndConditions {
    contentType: TermsContentType;
    linkContent: string;
    htmlContent: string;
}

//ACTIONS

//ACTION CREATORS

//REDUCER
const initialState: RestaurantDetailsState = {
    restaurant: {
        id: '',
        name: '',
        addressLine: '',
        zip: '',
        city: '',
        phoneNumber: '',
        email: '',
        website: '',
        latitude: 0,
        longitude: 0,
        contactPersons: [],
        paymentDetails: {
            firmName: '',
            firstName: '',
            lastName: '',
            address: '',
            zip: '',
            city: '',
            accountNumber: '',
            iban: '',
            vat: '',
            tax: 0,
            currency: '',
            thanksMessage: '',
        },
        timeZone: 'Europe/Zurich',
        qrCodesData: { dineInQrCodeName: '', takeAwayQrCodeName: '', homeDeliveryQrName: '' },
        dineIn: {
            isActive: false,
            workingHours: {
                monday: [],
                tuesday: [],
                wednesday: [],
                thursday: [],
                friday: [],
                saturday: [],
                sunday: [],
            },
            isDeliverAnytime: false,
            isPayToClose: false,
        },
        takeAway: {
            isActive: false,
            workingHours: {
                monday: [],
                tuesday: [],
                wednesday: [],
                thursday: [],
                friday: [],
                saturday: [],
                sunday: [],
            },
            isDeliverAnytime: false,
            isPayToClose: false,
        },
        homeDelivery: {
            isActive: false,
            workingHours: {
                monday: [],
                tuesday: [],
                wednesday: [],
                thursday: [],
                friday: [],
                saturday: [],
                sunday: [],
            },
            isDeliverAnytime: false,
            isPayToClose: false,
        },
        takeOut: {
            isActive: false,
            workingHours: {
                monday: [],
                tuesday: [],
                wednesday: [],
                thursday: [],
                friday: [],
                saturday: [],
                sunday: [],
            },
            isDeliverAnytime: false,
            isPayToClose: false,
        },
        pricingLevels: [],
        coolBoxData: {
            isCoolBox: false,
            coolBoxName: '',
            isPublic: false,
        },
        type: [],
        isDeliverAnytime: false,
        termsAndConditions: {
            contentType: TermsContentType.None,
            linkContent: '',
            htmlContent: '',
        },
        description: '',
    },
    restaurantInformation: {
        name: '',
        id: '',
        addressLine: '',
        zip: '',
        city: '',
        phoneNumber: '',
        email: '',
        website: '',
        latitude: 0,
        longitude: 0,
        contactPersons: [],
        paymentDetails: {
            firmName: '',
            firstName: '',
            lastName: '',
            address: '',
            zip: '',
            city: '',
            accountNumber: '',
            iban: '',
            vat: '',
            tax: 0,
            currency: '',
            thanksMessage: '',
        },
        timeZone: 'Europe/Zurich',
        qrCodesData: { dineInQrCodeName: '', takeAwayQrCodeName: '', homeDeliveryQrName: '' },
        dineIn: {
            isActive: false,
            workingHours: {
                monday: [],
                tuesday: [],
                wednesday: [],
                thursday: [],
                friday: [],
                saturday: [],
                sunday: [],
            },
            isDeliverAnytime: false,
            isPayToClose: false,
        },
        takeAway: {
            isActive: false,
            workingHours: {
                monday: [],
                tuesday: [],
                wednesday: [],
                thursday: [],
                friday: [],
                saturday: [],
                sunday: [],
            },
            isDeliverAnytime: false,
            isPayToClose: false,
        },
        homeDelivery: {
            isActive: false,
            workingHours: {
                monday: [],
                tuesday: [],
                wednesday: [],
                thursday: [],
                friday: [],
                saturday: [],
                sunday: [],
            },
            isDeliverAnytime: false,
            isPayToClose: false,
        },
        takeOut: {
            isActive: false,
            workingHours: {
                monday: [],
                tuesday: [],
                wednesday: [],
                thursday: [],
                friday: [],
                saturday: [],
                sunday: [],
            },
            isDeliverAnytime: false,
            isPayToClose: false,
        },
        pricingLevels: [],
        coolBoxData: {
            isCoolBox: false,
            coolBoxName: '',
            isPublic: false,
        },
        type: [],
        isDeliverAnytime: false,
        termsAndConditions: {
            contentType: TermsContentType.None,
            linkContent: '',
            htmlContent: '',
        },
        description: '',
    },
    cuisineTypes: [],
    taxes: {
        id: '',
        standardRate: 0,
        reducedRate: 0,
        specialRateForAccommodation: 0,
        noVAT: 0,
    },
    stornoReasons: [],
    qrCodes: [],
    qrCodesLoading: true,
    responsibleUsers: [],
    groups: [],
    managedGroups: [],
};

export const reducer: Reducer<RestaurantDetailsState> = (
    state: RestaurantDetailsState | undefined,
    incomingAction: Action
): RestaurantDetailsState => {
    if (state === undefined) {
        return initialState;
    }
    const action = incomingAction as RestaurantDetailsAction;

    switch (action.type) {
        case 'RESTAURANT_DETAILS_GET_SUCCESS':
            return {
                ...state,
                restaurant: action.restaurant,
            };
        case 'RESTAURANT_DETAILS_UPDATE_SUCCESS':
            return {
                ...state,
                restaurant: action.restaurant,
            };
        case 'GET_RESTAURANT_INFORMATION_SUCCESS': {
            return {
                ...state,
                restaurantInformation: action.restaurantInformation,
            };
        }
        case 'GET_CUISINE_TYPES_SUCCESS': {
            return {
                ...state,
                cuisineTypes: action.data,
            };
        }
        case 'UPDATE_RESTAURANT_INFORMATION_SUCCESS': {
            return {
                ...state,
                restaurantInformation: action.restaurantInformation,
            };
        }
        case 'GET_QR_CODES_SUCCESS': {
            return {
                ...state,
                qrCodes: action.data,
                qrCodesLoading: false,
            };
        }
        case 'DELETE_QR_CODE_SUCCESS': {
            //@ts-ignore
            const newQrCodes = state.qrCodes.filter((qrCode) => !action.data.includes(qrCode.id));
            return {
                ...state,
                qrCodes: newQrCodes,
            };
        }
        case 'UPDATE_QR_CODE_SUCCESS': {
            const restOfQrs = state.qrCodes.filter((qrCode) => qrCode.id !== action.data.id);
            return {
                ...state,
                qrCodes: [action.data, ...restOfQrs],
            };
        }
        case 'UPDATE_CONTACT_PERSONS': {
            return {
                ...state,
                restaurantInformation: {
                    ...state.restaurantInformation,
                    contactPersons: state.restaurantInformation.contactPersons.map((x) =>
                        action.users.find((y) => y.id === x.id)
                            ? action.users.find((y) => y.id === x.id)!
                            : x
                    ),
                },
            };
        }
        case 'UPDATE_PAYMENT_DETAILS_SUCCESS': {
            return {
                ...state,
                restaurantInformation: {
                    ...state.restaurantInformation,
                    paymentDetails: action.data,
                },
            };
        }
        case 'UPDATE_QR_CODES_NAME_SUCCESS': {
            return {
                ...state,
                restaurantInformation: { ...state.restaurantInformation, qrCodesData: action.data },
            };
        }
        case 'GET_RESTAURANT_RESPONSIBLE_USERS_SUCCESS': {
            return {
                ...state,
                responsibleUsers: action.data,
            };
        }
        case 'GET_TAXES_SUCCESS': {
            return {
                ...state,
                taxes: action.data,
            };
        }
        case 'ADD_PRICING_LEVEL_SUCCESS': {
            return {
                ...state,
                restaurantInformation: {
                    ...state.restaurantInformation,
                    pricingLevels: [...state.restaurantInformation.pricingLevels, action.payload],
                },
            };
        }
        case 'UPDATE_PRICING_LEVEL_SUCCESS': {
            return {
                ...state,
                restaurantInformation: {
                    ...state.restaurantInformation,
                    pricingLevels: state.restaurantInformation.pricingLevels.map((x) =>
                        x.id === action.payload.id ? action.payload : x
                    ),
                },
            };
        }
        case 'DELETE_PRICING_LEVEL_SUCCESS': {
            return {
                ...state,
                restaurantInformation: {
                    ...state.restaurantInformation,
                    pricingLevels: state.restaurantInformation.pricingLevels.filter(
                        (x) => x.id !== action.payload
                    ),
                },
            };
        }
        case 'ADD_GROUP_PRICING_LEVEL_SUCCESS': {
            return {
                ...state,
                restaurantInformation: {
                    ...state.restaurantInformation,
                    groupPricingLevels: [
                        ...(state.restaurantInformation.groupPricingLevels ?? []),
                        action.payload,
                    ],
                    managedGroupPricingLevels: [
                        ...(state.restaurantInformation.managedGroupPricingLevels ?? []),
                        action.payload,
                    ],
                },
            };
        }
        case 'UPDATE_GROUP_PRICING_LEVEL_SUCCESS': {
            const renameLevel = (x: IdName) => (x.id === action.payload.id ? action.payload : x);
            return {
                ...state,
                restaurantInformation: {
                    ...state.restaurantInformation,
                    groupPricingLevels: (state.restaurantInformation.groupPricingLevels ?? []).map(
                        renameLevel
                    ),
                    managedGroupPricingLevels: (
                        state.restaurantInformation.managedGroupPricingLevels ?? []
                    ).map(renameLevel),
                },
            };
        }
        case 'DELETE_GROUP_PRICING_LEVEL_SUCCESS': {
            const isNotDeleted = (x: IdName) => x.id !== action.payload;
            return {
                ...state,
                restaurantInformation: {
                    ...state.restaurantInformation,
                    groupPricingLevels: (
                        state.restaurantInformation.groupPricingLevels ?? []
                    ).filter(isNotDeleted),
                    managedGroupPricingLevels: (
                        state.restaurantInformation.managedGroupPricingLevels ?? []
                    ).filter(isNotDeleted),
                },
            };
        }
        case 'UPDATE_RESTAURANT_ADVANCED_SETTINGS_SUCCESS': {
            return {
                ...state,
                restaurantInformation: {
                    ...state.restaurantInformation,
                    advancedSettings: action.data,
                },
            };
        }
        case 'GET_STORNO_REASONS_SUCCESS': {
            return {
                ...state,
                stornoReasons: action.data,
            };
        }
        case 'GET_MY_GROUPS': {
            const sorted = action.payload.sort((a, b) => a.name.localeCompare(b.name));
            sorted.forEach((g) => g.restaurants.sort((a, b) => a.name.localeCompare(b.name)));
            return {
                ...state,
                groups: sorted,
            };
        }
        case 'GET_CURRENT_RESTAURANT_MANAGED_GROUPS': {
            const sorted = action.payload.sort((a, b) => a.name.localeCompare(b.name));
            sorted.forEach((g) => g.restaurants.sort((a, b) => a.name.localeCompare(b.name)));
            return {
                ...state,
                managedGroups: sorted,
            };
        }
        default:
            return state;
    }
};
